import { datadogRum } from '@datadog/browser-rum'
import { LegacyDropdownKV } from '@thyme/nashville/src/types/dropdowns'
import { AllIcons } from '@thyme/nashville/src/types/icons'
import { SelectOptions } from '@thyme/nashville/src/types/select'
import capitalize from 'lodash-es/capitalize'
import { Subtask, SubtaskStatus } from '@/api/pathways/subtasks/types'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { IdMap } from '@/legacy/types/api/store'
import { Entity } from '@/legacy/types/entities/entities'
import { NotificationType } from '@/legacy/types/notifications'
import { SubtaskStatuses } from '@/legacy/types/pathways/pathways'
import {
  closedSubtaskStatuses,
  openSubtaskStatuses,
  SubtaskPriority,
  SubtaskFlaggedType,
  SubtaskStatusOption,
  PriorityChangePayload,
} from '@/legacy/types/pathways/subtasks'
import { compareNullableDatetimes, stringToDateTime } from '@/libs/date/date'
import { humanizeRole } from '@/libs/entities/users/roles'
import {
  formatDateTime,
  formatDateTimeWithTime,
  formatNameFromEntity,
} from '@/libs/format/format'
import { safeLookup } from '@/libs/lookup/lookup'

const FLAGGED_TYPE_ICON_COLOR = 'nash-tigerlily700'
const DEFAULT_ICON_COLOR = 'nash-neutral500'

const PRIORITY_ICONS: Record<SubtaskPriority, string> = {
  [SubtaskPriority.URGENT]: AllIcons.UrgentPriority,
  [SubtaskPriority.HIGH]: AllIcons.HighPriority,
  [SubtaskPriority.ROUTINE]: AllIcons.RoutinePriority,
  [SubtaskPriority.OPTIONAL]: AllIcons.OptionalPriority,
}

/**
 * Track when a user sets or unsets Care Team Flagged status
 * Call this after buildPriorityChangePayload succeeds but before submitting the update
 * @param payload - The priority change payload from buildPriorityChangePayload
 * @param sourceComponent - The component/location where the change occurred
 * @param userEntity - The user entity making the change (contains email, name, ID)
 * @param subtaskId - Optional subtask ID being flagged/unflagged (not available for creates)
 * @param subtaskTitle - Optional subtask title for additional context
 */
export function maybeTrackCareTeamFlaggedChange(
  payload: PriorityChangePayload,
  sourceComponent: string,
  userEntity: Entity | null | undefined,
  subtaskId?: string,
  subtaskTitle?: string
) {
  if (!userEntity) return

  const userEmail = userEntity.email
  const userName = userEntity.person
    ? `${userEntity.person.firstName} ${userEntity.person.lastName}`
    : 'Unknown User Name'

  const data = {
    sourceComponent,
    userEmail,
    userName,
    userId: userEntity.entityId,
    ...(subtaskId ? { subtaskId } : {}),
    ...(subtaskTitle ? { subtaskTitle } : {}),
  }

  if (payload.flaggedType === SubtaskFlaggedType.CARE_TEAM_FLAGGED) {
    datadogRum.addAction('careTeamFlaggedSet', data)
  } else if (
    payload.flaggedType === null &&
    payload.flaggedBy === null &&
    payload.priority
  ) {
    datadogRum.addAction('careTeamFlaggedUnset', {
      ...data,
      newPriority: payload.priority,
    })
  }
}

// Display priority can be an actual priority or the flagged type
export type DisplayPriority = SubtaskPriority | SubtaskFlaggedType | null

export const CARE_TEAM_FLAGGED_LABEL = 'Care Team Flagged'

export const reattemptReasonPlaceholder = 'Reason for reattempting subtask...'
export const datepickerPlaceholder = 'mm/dd/yyyy'

export const unsetPriority = {
  val: null,
  label: 'Set priority for all',
  icon: getPriorityIcon(null),
}

export const unsetAssignee = {
  val: null,
  label: 'Set assignee for all',
  icon: null,
}

export const unsetStatus = {
  val: null,
  label: 'Set status for all',
  icon: null,
}

export const unsetSubStatus = {
  value: null,
  label: 'Select a reason',
}

export const unsetRole = {
  val: null,
  label: 'Set role for all',
  icon: null,
}

export const allTasksBulkEditSubtaskStatusOptions = [
  {
    val: SubtaskStatus.OPEN_UNASSIGNED,
    label: SubtaskStatuses[SubtaskStatus.OPEN_UNASSIGNED].text,
    icon: SubtaskStatuses[SubtaskStatus.OPEN_UNASSIGNED].icon,
    fillType: SubtaskStatuses[SubtaskStatus.OPEN_UNASSIGNED].fillType,
  },
  {
    val: SubtaskStatus.CANCELLED,
    label: SubtaskStatuses[SubtaskStatus.CANCELLED].text,
    icon: SubtaskStatuses[SubtaskStatus.CANCELLED].icon,
    fillType: SubtaskStatuses[SubtaskStatus.CANCELLED].fillType,
  },
  {
    val: SubtaskStatus.ON_HOLD,
    label: SubtaskStatuses[SubtaskStatus.ON_HOLD].text,
    icon: SubtaskStatuses[SubtaskStatus.ON_HOLD].icon,
    fillType: SubtaskStatuses[SubtaskStatus.ON_HOLD].fillType,
  },
  {
    val: SubtaskStatus.REATTEMPT,
    label: SubtaskStatuses[SubtaskStatus.REATTEMPT].text,
    icon: SubtaskStatuses[SubtaskStatus.REATTEMPT].icon,
    fillType: SubtaskStatuses[SubtaskStatus.REATTEMPT].fillType,
  },
]

/**
 *
 * @param priority
 */
export function getPriorityIcon(priority: SubtaskPriority | null) {
  return priority === null ? AllIcons.EmptyPriority : PRIORITY_ICONS[priority]
}

/**
 * Returns what should be displayed as the "priority" for a subtask.
 * If the subtask is flagged by the care team (and FF enabled), returns the flagged type,
 * otherwise returns the actual priority.
 * @param subtask
 * @param showCareTeamFlagged
 */
export function getDisplayPriority(
  subtask: Subtask,
  showCareTeamFlagged: boolean
): DisplayPriority {
  if (
    showCareTeamFlagged &&
    subtask.flaggedType === SubtaskFlaggedType.CARE_TEAM_FLAGGED
  ) {
    return SubtaskFlaggedType.CARE_TEAM_FLAGGED
  }
  return subtask.priority
}

/**
 *
 * @param value
 */
function getIconForDisplayValue(value: DisplayPriority) {
  return value === SubtaskFlaggedType.CARE_TEAM_FLAGGED
    ? AllIcons.exclamationSquareFill
    : getPriorityIcon(value)
}

/**
 *
 * @param subtask
 * @param showCareTeamFlagged
 */
export function getDisplayPriorityIcon(
  subtask: Subtask,
  showCareTeamFlagged: boolean
) {
  return getIconForDisplayValue(
    getDisplayPriority(subtask, showCareTeamFlagged)
  )
}

/**
 * Converts a display priority to its display label
 * @param displayPriority
 */
export function getDisplayPriorityLabel(
  displayPriority: DisplayPriority
): string {
  if (displayPriority === SubtaskFlaggedType.CARE_TEAM_FLAGGED) {
    return CARE_TEAM_FLAGGED_LABEL
  }
  return capitalize((displayPriority ?? '').toLowerCase())
}

/**
 * Returns the icon color for a display priority
 * @param displayPriority
 */
export function getDisplayPriorityIconColor(
  displayPriority: DisplayPriority
): string {
  return displayPriority === SubtaskFlaggedType.CARE_TEAM_FLAGGED
    ? FLAGGED_TYPE_ICON_COLOR
    : DEFAULT_ICON_COLOR
}

/**
 * Builds the update payload when user changes priority selection
 *
 * Care Team Flagged operates as a pseudo-priority that sets flaggedType/flaggedBy without changing priority.
 * When setting a real priority, always clears the flagged fields (backend treats them as companion metadata).
 * @param selectedValue - The priority or CARE_TEAM_FLAGGED being set
 * @param currentUserId - Current user's ID (required when setting CARE_TEAM_FLAGGED)
 * @returns Payload with priority and/or flagged fields to update, or null if validation fails
 */
export function buildPriorityChangePayload(
  selectedValue: SubtaskPriority | SubtaskFlaggedType,
  currentUserId: string | undefined
): PriorityChangePayload | null {
  if (selectedValue === SubtaskFlaggedType.CARE_TEAM_FLAGGED) {
    if (!currentUserId) {
      useNotificationStore().setNotification({
        message: 'Unable to update priority. Please try again.',
        type: NotificationType.DANGER,
        error: 'Cannot set Care Team Flagged without a valid user ID.',
      })
      return null
    }
    return {
      flaggedType: SubtaskFlaggedType.CARE_TEAM_FLAGGED,
      flaggedBy: currentUserId,
    }
  }

  return {
    priority: selectedValue,
    flaggedType: null,
    flaggedBy: null,
  }
}

/**
 * Returns priority dropdown options, optionally including Care Team Flagged as a pseudo-priority option
 * @param showCareTeamFlagged
 */
export function getSubtaskPriorityOptions(showCareTeamFlagged: boolean) {
  const baseOptions = Object.entries(SubtaskPriority).map(([, enumValue]) => ({
    val: enumValue,
    label: capitalize(enumValue),
    icon: getPriorityIcon(enumValue),
    fillType: DEFAULT_ICON_COLOR,
  }))

  if (showCareTeamFlagged) {
    return [
      {
        val: SubtaskFlaggedType.CARE_TEAM_FLAGGED,
        label: CARE_TEAM_FLAGGED_LABEL,
        icon: AllIcons.exclamationSquareFill,
        fillType: FLAGGED_TYPE_ICON_COLOR,
      },
      ...baseOptions,
    ]
  }

  return baseOptions
}

/**
 *
 * @param selectedPriorityObj
 */
export function getSelectedPriorityIcon(
  selectedPriorityObj: { val: DisplayPriority } | null
): string | null {
  return selectedPriorityObj
    ? getIconForDisplayValue(selectedPriorityObj.val)
    : null
}

/**
 *
 * @param selectedPriorityObj
 */
export function getSelectedPriorityIconFill(
  selectedPriorityObj: { val: DisplayPriority } | null
): string | null {
  return selectedPriorityObj
    ? getDisplayPriorityIconColor(selectedPriorityObj.val)
    : null
}

export const subtaskStatusOptions = (
  statusOptionsEnumsArr: SubtaskStatus[]
): LegacyDropdownKV[] => {
  const subtaskStatusObjArr: SubtaskStatusOption[] =
    Object.values(SubtaskStatuses)
  return subtaskStatusObjArr
    .map(
      ({
        icon,
        text,
        value,
        fillType,
      }: {
        icon: string
        text: string
        value: SubtaskStatus
        fillType: string | null
      }): LegacyDropdownKV => [value as string, { icon, text, fillType }]
    )
    .filter((statusOption: Array<any>) =>
      statusOptionsEnumsArr.includes(statusOption[0] as SubtaskStatus)
    )
}

/**
 *
 * Helper function to render staff name from ID
 * @param staffId
 * @param staff
 */
export function staffName(staffId: string, staff: IdMap<Entity>): string {
  const entity = safeLookup(staffId, staff) as Entity
  return formatNameFromEntity(entity)
    ? formatNameFromEntity(entity)
    : '<Unknown User>'
}

/**
 *
 * Helper function to render role or staff name string
 * @param selectedSubtask
 * @param staff
 */
export function renderRoleOrStaff(
  selectedSubtask: Subtask,
  staff: IdMap<Entity>
) {
  if (selectedSubtask.responsibleStaffId) {
    return staffName(selectedSubtask.responsibleStaffId, staff)
  }
  return selectedSubtask.responsibleRole
    ? humanizeRole(selectedSubtask.responsibleRole)
    : 'N/A'
}

/**
 *
 * @param currentStatus
 * @param canOverrideBlockedStatus
 */
export function getAvailableStatusOptions(
  currentStatus: SubtaskStatus,
  canOverrideBlockedStatus = false
): SelectOptions {
  let statusOptions = [...SubtaskStatuses[currentStatus].statusOptions]

  if (canOverrideBlockedStatus && currentStatus === SubtaskStatus.BLOCKED) {
    statusOptions = [
      ...SubtaskStatuses[SubtaskStatus.OPEN_UNASSIGNED].statusOptions,
    ]
  }

  if (
    currentStatus === SubtaskStatus.OPEN_ASSIGNED &&
    !statusOptions.includes(SubtaskStatus.REATTEMPT)
  ) {
    statusOptions.push(SubtaskStatus.REATTEMPT)
  }
  if (
    currentStatus === SubtaskStatus.OPEN_UNASSIGNED &&
    !statusOptions.includes(SubtaskStatus.COMPLETED)
  ) {
    statusOptions.push(SubtaskStatus.COMPLETED)
  }

  return subtaskStatusOptions(statusOptions)
}

export const getSubtaskIconName = (subtaskStatus: SubtaskStatus) => {
  if (subtaskStatus === SubtaskStatus.CANCELLED) {
    return SubtaskStatuses.CANCELLED.icon
  }
  return SubtaskStatuses[subtaskStatus as keyof typeof SubtaskStatuses].icon
}

export const getSubtaskIconFill = (subtaskStatus: SubtaskStatus) => {
  if (subtaskStatus === SubtaskStatus.CANCELLED) {
    return SubtaskStatuses.CANCELLED.fillType
  }
  return SubtaskStatuses[subtaskStatus as keyof typeof SubtaskStatuses].fillType
}
/**
 *
 * @param isOverdue
 * @param status
 */
export function renderDatePretext(isOverdue: boolean, status: SubtaskStatus) {
  return isOverdue
    ? 'Overdue'
    : status === SubtaskStatuses.COMPLETED.value
      ? 'Completed'
      : status === SubtaskStatuses.CANCELLED.value
        ? 'Canceled'
        : status === SubtaskStatuses.CLOSED_IN_ERROR.value
          ? 'Closed in error'
          : 'Due'
}

/**
 *
 * @param subtask
 */
export function isSubtaskOverdue(subtask: Subtask) {
  if (closedSubtaskStatuses.includes(subtask.status)) return false
  const subtaskDueDate = new Date(subtask.dueDatetime)
  const today = new Date()
  return !!subtask.dueDatetime && subtaskDueDate < today
}

/**
 *
 * @param root0
 * @param root0.status
 * @param root0.statusUpdatedAt
 * @param root0.dueDatetime
 */
export function renderDateString({
  status,
  statusUpdatedAt,
  dueDatetime,
}: Subtask) {
  return closedSubtaskStatuses.includes(status)
    ? formatDateTimeWithTime(stringToDateTime(statusUpdatedAt ?? ''))
    : dueDatetime
      ? formatDateTime(stringToDateTime(dueDatetime ?? ''))
      : 'N/A'
}

/**
 *
 * @param s
 */
export function isClosedSubtask(s: Subtask) {
  const status = s.status
  return closedSubtaskStatuses.includes(status)
}

/**
 *
 * @param s
 * @param selfEntity
 */
export function isAssignedToSelf(
  s: Subtask | null,
  selfEntity: Entity | null
): boolean {
  if (s && selfEntity) {
    return s.responsibleStaffId === selfEntity.entityId
  }
  return false
}

/**
 *
 * @param subtasks
 */
export function sortSubtasks(subtasks: Subtask[]) {
  return subtasks.sort((a: Subtask, b: Subtask) => {
    const statuses = Object.values(SubtaskStatus)

    const isConsideredOpenStatus =
      openSubtaskStatuses.includes(a.status) &&
      openSubtaskStatuses.includes(b.status)

    const statusSort = isConsideredOpenStatus
      ? false
      : statuses.indexOf(a.status) - statuses.indexOf(b.status)

    if (statusSort) {
      return statusSort
    }
    if (!isClosedSubtask(a)) {
      const dueDatetimeSort = compareNullableDatetimes(
        stringToDateTime(a.dueDatetime),
        stringToDateTime(b.dueDatetime)
      )
      if (dueDatetimeSort) {
        return dueDatetimeSort
      }
      return 0
    }

    return compareNullableDatetimes(
      stringToDateTime(a.statusUpdatedAt),
      stringToDateTime(b.statusUpdatedAt)
    )
  })
}

/**
 *
 * @param subtasks
 */
export function filterReattemptedSubtasks(subtasks: Subtask[]) {
  const today = new Date()
  return subtasks.filter((subtask: Subtask) => {
    if (
      subtask.hideDatetime &&
      subtask.status === SubtaskStatus.OPEN_UNASSIGNED
    ) {
      return subtask.hideDatetime < today.toISOString()
    } else if (
      subtask.hideDatetime &&
      subtask.status !== SubtaskStatus.OPEN_UNASSIGNED
    ) {
      return true
    }
    return !subtask.hideDatetime
  })
}

/**
 *
 * @param a
 */
export function effectiveSubtaskDate(a: Subtask) {
  let date: string | undefined
  if (a.status === SubtaskStatuses.COMPLETED.value) {
    date = a.statusUpdatedAt
  } else {
    date = a.dueDatetime
  }
  return date
}
